import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Crown, EyeOff, Mail, RotateCcw, Shield, Trash2, UserRound, UserX } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';
import { useApp } from '@/contexts/AppContext';
import { useUserRole } from '@/hooks/useUserRole';
import { cn } from '@/lib/utils';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppFieldGroup,
  AppHeaderBar,
  AppLoadingState,
  AppPage,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../ui-foundation';
import {
  AppAvatar as Avatar,
  AppAvatarFallback as AvatarFallback,
  AppAvatarImage as AvatarImage,
} from '../../ui-foundation/adapters/web';

type ProfileRow = Tables<'profiles'>;
type CommunityPost = Tables<'community_posts'>;
type CommunityReply = Tables<'community_replies'>;
type CommunityReport = Tables<'community_reports'>;
type CommunityModerationLog = Tables<'community_moderation_log'>;
type CommunityModerationAppeal = Tables<'community_moderation_appeals'>;
type RoleRow = Pick<Tables<'user_roles'>, 'id' | 'role'>;

type MemberReportItem = {
  report: CommunityReport;
  targetType: 'post' | 'reply';
  preview: string;
};

function statusTone(status: ProfileRow['community_access_status']) {
  if (status === 'suspended') return 'bg-amber-100 text-amber-800';
  if (status === 'banned') return 'bg-destructive/10 text-destructive';
  return 'bg-green-100 text-green-800';
}

function formatActionLabel(action: string) {
  return action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function reportStatusMeta(status: CommunityReport['status']) {
  if (status === 'in_review') return 'bg-amber-100 text-amber-800';
  if (status === 'actioned') return 'bg-primary/10 text-primary';
  if (status === 'dismissed') return 'bg-muted text-muted-foreground';
  return 'bg-rose-100 text-rose-800';
}

function getHighestRole(roles: Array<'admin' | 'moderator' | 'user'>) {
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('moderator')) return 'moderator';
  return 'user';
}

function getRoleSaveErrorMessage(error: unknown) {
  const code =
    typeof error === 'object' && error && 'code' in error ? String((error as { code?: string }).code ?? '') : '';
  const message =
    typeof error === 'object' && error && 'message' in error
      ? String((error as { message?: string }).message ?? '')
      : '';
  const details =
    typeof error === 'object' && error && 'details' in error
      ? String((error as { details?: string }).details ?? '')
      : '';
  const combined = `${code} ${message} ${details}`.toLowerCase();

  if (
    combined.includes('aal2') ||
    combined.includes('mfa') ||
    combined.includes('authenticator') ||
    combined.includes('assurance')
  ) {
    return 'This action requires a verified admin session. Complete account verification and try again.';
  }

  if (
    code === '42501' ||
    combined.includes('permission denied') ||
    combined.includes('row-level security') ||
    combined.includes('not allowed')
  ) {
    return 'You do not have permission to change roles.';
  }

  return 'Role could not be saved. Please try again.';
}

function getMemberLabel(firstName: string | null | undefined, email?: string | null) {
  const trimmedFirstName = firstName?.trim();
  if (trimmedFirstName) return trimmedFirstName;

  const trimmedEmailLocalPart = email?.split('@')[0]?.trim();
  if (trimmedEmailLocalPart) return trimmedEmailLocalPart;

  return 'Adulting member';
}

function MemberSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <AppSectionHeader title={title} subtitle={subtitle} />
      <div className="mt-3 space-y-3">{children}</div>
    </div>
  );
}

export default function AdminCommunityMemberPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user } = useApp();
  const { isAdmin, loading: roleLoading } = useUserRole();

  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(false);
  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [roleRows, setRoleRows] = useState<RoleRow[]>([]);
  const [memberRole, setMemberRole] = useState<'admin' | 'moderator' | 'user'>('user');
  const [newRole, setNewRole] = useState<'admin' | 'moderator' | 'user'>('user');
  const [memberEmail, setMemberEmail] = useState<string | null>(null);
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [replies, setReplies] = useState<CommunityReply[]>([]);
  const [reports, setReports] = useState<MemberReportItem[]>([]);
  const [log, setLog] = useState<CommunityModerationLog[]>([]);
  const [appeals, setAppeals] = useState<CommunityModerationAppeal[]>([]);
  const [note, setNote] = useState('');
  const [memberMessage, setMemberMessage] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [deletingMember, setDeletingMember] = useState(false);

  const loadMember = useCallback(async () => {
    if (!userId || !isAdmin) return;

    setLoading(true);

    const [profileResult, roleResult, postsResult, repliesResult, logResult, contactResult] = await Promise.all([
      supabase.from('profiles').select('*').eq('user_id', userId).maybeSingle(),
      supabase.from('user_roles').select('id, role').eq('user_id', userId).order('id'),
      supabase.from('community_posts').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(6),
      supabase.from('community_replies').select('*').eq('user_id', userId).order('created_at', { ascending: false }).limit(6),
      supabase.from('community_moderation_log').select('*').eq('target_user_id', userId).order('created_at', { ascending: false }).limit(20),
      supabase.rpc('get_admin_member_contact' as any, { target_user_id: userId }),
    ]);

    if (profileResult.error || postsResult.error || repliesResult.error || logResult.error || roleResult.error || contactResult.error) {
      console.error(profileResult.error || postsResult.error || repliesResult.error || logResult.error || roleResult.error || contactResult.error);
      toast.error('Could not load that member.');
      setLoading(false);
      return;
    }

    const nextPosts = (postsResult.data || []) as CommunityPost[];
    const nextReplies = (repliesResult.data || []) as CommunityReply[];
    const postIds = nextPosts.map((post) => post.id);
    const replyIds = nextReplies.map((reply) => reply.id);

    const reportQueries = await Promise.all([
      postIds.length
        ? supabase.from('community_reports').select('*').in('post_id', postIds).order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
      replyIds.length
        ? supabase.from('community_reports').select('*').in('reply_id', replyIds).order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
      (logResult.data || []).length
        ? supabase
            .from('community_moderation_appeals')
            .select('*')
            .in('moderation_log_id', ((logResult.data || []) as CommunityModerationLog[]).map((entry) => entry.id))
            .order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (reportQueries.some((result) => result.error)) {
      console.error(reportQueries);
      toast.error('Could not load report history for that member.');
      setLoading(false);
      return;
    }

    const postLookup = new Map(nextPosts.map((post) => [post.id, post.body]));
    const replyLookup = new Map(nextReplies.map((reply) => [reply.id, reply.body]));
    const nextReports = [
      ...((reportQueries[0].data || []) as CommunityReport[]).map((report) => ({
        report,
        targetType: 'post' as const,
        preview: report.post_id ? (postLookup.get(report.post_id) || 'Reported post no longer available.') : 'Reported post no longer available.',
      })),
      ...((reportQueries[1].data || []) as CommunityReport[]).map((report) => ({
        report,
        targetType: 'reply' as const,
        preview: report.reply_id ? (replyLookup.get(report.reply_id) || 'Reported reply no longer available.') : 'Reported reply no longer available.',
      })),
    ].sort((a, b) => new Date(b.report.created_at).getTime() - new Date(a.report.created_at).getTime());

    const nextRoleRows = ((roleResult.data || []) as RoleRow[]);
    const nextRole = getHighestRole(nextRoleRows.map((item) => item.role));

    setProfile(profileResult.data as ProfileRow | null);
    setRoleRows(nextRoleRows);
    setMemberRole(nextRole);
    setNewRole(nextRole);
    setMemberEmail(((contactResult.data as { email?: string | null } | null)?.email) ?? null);
    setPosts(nextPosts);
    setReplies(nextReplies);
    setReports(nextReports.slice(0, 12));
    setLog((logResult.data || []) as CommunityModerationLog[]);
    setAppeals((reportQueries[2].data || []) as CommunityModerationAppeal[]);
    setLoading(false);
  }, [isAdmin, userId]);

  useEffect(() => {
    if (!roleLoading && !isAdmin) navigate('/admin', { replace: true });
  }, [isAdmin, navigate, roleLoading]);

  useEffect(() => {
    void loadMember();
  }, [loadMember]);

  const warningCount = useMemo(
    () => log.filter((entry) => entry.action === 'member_warned').length,
    [log]
  );

  const unresolvedReportCount = useMemo(
    () => reports.filter((item) => item.report.status === 'open' || item.report.status === 'in_review').length,
    [reports]
  );

  const openAppealCount = useMemo(
    () => appeals.filter((appeal) => appeal.status === 'open').length,
    [appeals]
  );

  const runMemberAction = async (
    action: 'member_warned' | 'member_suspended' | 'member_restored' | 'member_banned',
    nextStatus?: 'active' | 'suspended' | 'banned',
  ) => {
    if (!user || !userId || !profile) return;

    setActioning(true);

    const operations: Promise<any>[] = [
      supabase.from('community_moderation_log').insert({
        target_user_id: userId,
        moderator_user_id: user.id,
        action,
        note: note.trim() || null,
        member_visible: true,
        member_message: memberMessage.trim() || null,
      } as TablesInsert<'community_moderation_log'>),
    ];

    if (nextStatus) {
      operations.push(
        supabase
          .from('profiles')
          .update({ community_access_status: nextStatus })
          .eq('user_id', userId)
      );
    }

    const results = await Promise.all(operations);

    if (results.some((result) => result.error)) {
      console.error(results);
      toast.error('Could not save that moderation action.');
    } else {
      toast.success(
        action === 'member_warned'
          ? 'Warning recorded.'
          : action === 'member_suspended'
            ? 'Community access suspended.'
            : action === 'member_banned'
              ? 'Community access banned.'
            : 'Community access restored.'
      );
      setNote('');
      setMemberMessage('');
      await loadMember();
    }

    setActioning(false);
  };

  const saveRole = async () => {
    if (!userId || newRole === memberRole || savingRole) return;

    setSavingRole(true);

    try {
      const { data: currentRoleRows, error: loadError } = await supabase
        .from('user_roles')
        .select('id, role')
        .eq('user_id', userId)
        .order('id');

      if (loadError) {
        throw loadError;
      }

      const existingRoleRows = (((currentRoleRows && currentRoleRows.length > 0) ? currentRoleRows : roleRows) ||
        []) as RoleRow[];
      const matchingRow = existingRoleRows.find((entry) => entry.role === newRole);
      const primaryRowId = matchingRow?.id ?? existingRoleRows[0]?.id ?? null;

      if (!primaryRowId) {
        const { error: insertError } = await supabase.from('user_roles').insert({ user_id: userId, role: newRole });
        if (insertError) throw insertError;
      } else {
        if (!matchingRow) {
          const { error: updateError } = await supabase
            .from('user_roles')
            .update({ role: newRole })
            .eq('id', primaryRowId);
          if (updateError) throw updateError;
        }

        const staleRoleIds = existingRoleRows.filter((entry) => entry.id !== primaryRowId).map((entry) => entry.id);
        if (staleRoleIds.length > 0) {
          const { error: deleteError } = await supabase.from('user_roles').delete().in('id', staleRoleIds);
          if (deleteError) throw deleteError;
        }
      }

      const { data: savedRoleRows, error: refreshError } = await supabase
        .from('user_roles')
        .select('id, role')
        .eq('user_id', userId)
        .order('id');

      if (refreshError) throw refreshError;

      const nextRoleRows = ((savedRoleRows || []) as RoleRow[]);
      const nextRole = getHighestRole(nextRoleRows.map((entry) => entry.role));

      if (!nextRoleRows.length || nextRole !== newRole) {
        throw new Error('Role could not be saved. Please try again.');
      }

      setRoleRows(nextRoleRows);
      setMemberRole(nextRole);
      setNewRole(nextRole);
      toast.success('Role updated.');
    } catch (error) {
      console.error(error);
      toast.error(getRoleSaveErrorMessage(error));
    } finally {
      setSavingRole(false);
    }
  };

  const deleteMember = async () => {
    if (!userId || deletingMember) return;

    if (!window.confirm('Delete this member and their linked account data? This cannot be undone.')) {
      return;
    }

    setDeletingMember(true);

    const { error } = await supabase.rpc('admin_delete_member' as any, {
      target_user_id: userId,
      note_text: note.trim() || null,
    });

    setDeletingMember(false);

    if (error) {
      console.error(error);
      toast.error('Member could not be deleted. Please try again.');
      return;
    }

    toast.success('Member deleted.');
    navigate('/admin/community-members');
  };

  if (roleLoading || loading) {
    return (
      <AppPage className="min-h-screen items-center justify-center">
        <AppLoadingState
          title="Loading community member..."
          body="Fetching the profile, moderation history, reports, and appeals."
        />
      </AppPage>
    );
  }

  if (!profile || !isAdmin) return null;

  const roleLabel = memberRole === 'admin' ? 'Admin' : memberRole === 'moderator' ? 'Moderator' : 'Member';
  const accessLabel =
    profile.community_access_status === 'active'
      ? 'Active'
      : profile.community_access_status === 'suspended'
        ? 'Suspended'
        : 'Banned';
  const memberLabel = getMemberLabel(profile.first_name, memberEmail);

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <AppHeaderBar
          title="Community member"
          topPadding="$md"
          bottomPadding="$sm"
          action={
            <AppButton tone="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft size={16} />
              Back
            </AppButton>
          }
        />
      </div>

      <AppPage className="max-w-lg space-y-5 animate-fade-in">
        <AppCard>
          <AppCardBody className="space-y-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12 shrink-0 border border-border/70 bg-background">
              {profile.avatar_url ? <AvatarImage src={profile.avatar_url} alt={memberLabel} /> : null}
              <AvatarFallback className="bg-primary/15 font-semibold text-primary">
                {memberLabel.trim().charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <AppText className="text-sm font-semibold text-foreground">{memberLabel}</AppText>
              <AppText className="text-xs" color="$colorMuted">
                Joined {format(new Date(profile.created_at), 'd MMM yyyy')}
                {profile.postcode ? ` · ${profile.postcode}` : ''}
              </AppText>
              {memberEmail ? (
                <AppText className="text-xs" color="$colorMuted">
                  {memberEmail}
                </AppText>
              ) : null}
            </div>
            <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full', statusTone(profile.community_access_status))}>
              {accessLabel}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MetricCard label="Open reports" value={unresolvedReportCount} />
            <MetricCard label="Open appeals" value={openAppealCount} />
            <MetricCard label="Warnings logged" value={warningCount} />
            <MetricCard label="Posts" value={posts.length} />
            <MetricCard label="Replies" value={replies.length} />
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-[10px] font-medium bg-background border border-border px-2 py-1 rounded-full">
              Role: {roleLabel}
            </span>
            {memberEmail ? (
              <span className="text-[10px] font-medium bg-background border border-border px-2 py-1 rounded-full">
                <Mail size={12} className="mr-1 inline text-primary" />
                Contact ready
              </span>
            ) : null}
            {profile.community_access_status !== 'active' && (
              <span className="text-[10px] font-medium bg-background border border-border px-2 py-1 rounded-full">
                Community access paused
              </span>
            )}
          </div>
          </AppCardBody>
        </AppCard>

        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title="Member actions"
              subtitle="Keep this factual. Use warnings for boundary-setting, suspension for repeated or serious harm, and restore when the risk has been resolved."
            />
          </AppCardHeader>
          <AppCardBody>
            <AppFieldGroup label="Internal note" helper="Optional internal note for this action.">
              <AppTextArea
                value={note}
                onChangeText={setNote}
                maxLength={500}
                placeholder="Optional internal note for this action."
              />
            </AppFieldGroup>

            <AppFieldGroup label="Member-facing notice" helper="Optional message shown with this moderation action.">
              <AppTextArea
                value={memberMessage}
                onChangeText={setMemberMessage}
                maxLength={500}
                placeholder="Optional member-facing notice for this action."
              />
            </AppFieldGroup>

            <AppButtonRow>
              <AppButton
                tone="outline"
                size="sm"
                onClick={() => runMemberAction('member_warned')}
                disabled={actioning}
              >
              <Shield size={14} />
              Record warning
              </AppButton>
              {profile.community_access_status === 'active' ? (
                <AppButton
                  tone="outline"
                  size="sm"
                  onClick={() => runMemberAction('member_suspended', 'suspended')}
                  disabled={actioning}
                >
                <EyeOff size={14} />
                Suspend community access
                </AppButton>
              ) : null}
              {profile.community_access_status === 'suspended' ? (
                <AppButton
                  tone="destructive"
                  size="sm"
                  onClick={() => runMemberAction('member_banned', 'banned')}
                  disabled={actioning}
                >
                <UserX size={14} />
                Ban member
                </AppButton>
              ) : null}
              {profile.community_access_status !== 'active' ? (
                <AppButton
                  tone="outline"
                  size="sm"
                  onClick={() => runMemberAction('member_restored', 'active')}
                  disabled={actioning}
                >
                <RotateCcw size={14} />
                Restore community access
                </AppButton>
              ) : null}
              <AppButton
                tone="destructive"
                size="sm"
                onClick={() => void deleteMember()}
                disabled={deletingMember}
              >
                <Trash2 size={14} />
                {deletingMember ? 'Deleting…' : 'Delete member'}
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </AppCard>

        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title="Role access"
              subtitle="Admins can promote or demote the member without leaving the shared admin flow."
            />
          </AppCardHeader>
          <AppCardBody>
            <AppButtonRow>
              {(['user', 'moderator', 'admin'] as const).map((candidate) => (
                <AppButton
                  key={candidate}
                  tone={newRole === candidate ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setNewRole(candidate)}
                  disabled={savingRole}
                >
                  {candidate === 'user' ? (
                    <UserRound size={14} />
                  ) : candidate === 'moderator' ? (
                    <Shield size={14} />
                  ) : (
                    <Crown size={14} />
                  )}
                  {candidate === 'user' ? 'Member' : candidate.charAt(0).toUpperCase() + candidate.slice(1)}
                </AppButton>
              ))}
            </AppButtonRow>
            <AppButton
              size="sm"
              alignSelf="flex-start"
              onClick={() => void saveRole()}
              disabled={savingRole || newRole === memberRole}
            >
              {savingRole ? 'Saving…' : 'Save role'}
            </AppButton>
          </AppCardBody>
        </AppCard>

        <MemberSection title="Profile & contact">
          <AppCard>
            <AppCardBody className="space-y-2">
              <AppText className="text-sm font-medium text-foreground">
                Name: {memberLabel}
              </AppText>
              <AppText className="text-xs" color="$colorMuted">
                Role: {roleLabel}
              </AppText>
              <AppText className="text-xs" color="$colorMuted">
                Community access: {accessLabel}
              </AppText>
              {memberEmail ? (
                <AppText className="text-xs" color="$colorMuted">
                  Contact: {memberEmail}
                </AppText>
              ) : (
                <AppText className="text-xs" color="$colorMuted">
                  Contact: No email available from the current admin contact lookup.
                </AppText>
              )}
            </AppCardBody>
          </AppCard>
        </MemberSection>

        <MemberSection title="Recent reports">
          {reports.length === 0 ? (
            <AppCard>
              <AppCardBody>
                <AppText fontSize="$sm" color="$colorMuted">
                  No reports linked to this member yet.
                </AppText>
              </AppCardBody>
            </AppCard>
          ) : (
            reports.map((item) => (
              <AppCard key={item.report.id}>
                <AppCardBody className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <AppText className="text-sm font-medium text-foreground">{item.report.reason}</AppText>
                  <span className="text-[10px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    {item.targetType === 'post' ? 'Post' : 'Reply'}
                  </span>
                  <AppText className="text-[10px]" color="$colorMuted">
                    {formatDistanceToNow(new Date(item.report.created_at), { addSuffix: true })}
                  </AppText>
                  <span className={cn('text-[10px] font-medium px-2 py-0.5 rounded-full', reportStatusMeta(item.report.status))}>
                    {item.report.status.replace(/_/g, ' ')}
                  </span>
                </div>
                <AppText className="whitespace-pre-wrap text-xs leading-relaxed" color="$colorMuted">{item.preview}</AppText>
                {item.report.details && <AppText className="text-xs" color="$colorMuted">Reporter note: {item.report.details}</AppText>}
                {item.report.resolution && <AppText className="text-xs" color="$colorMuted">Resolution: {item.report.resolution.replace(/_/g, ' ')}</AppText>}
                </AppCardBody>
              </AppCard>
            ))
          )}
        </MemberSection>

        <MemberSection title="Moderation timeline">
          {log.length === 0 ? (
            <AppCard>
              <AppCardBody>
                <AppText fontSize="$sm" color="$colorMuted">
                  No moderation actions recorded yet.
                </AppText>
              </AppCardBody>
            </AppCard>
          ) : (
            log.map((entry) => (
              <AppCard key={entry.id}>
                <AppCardBody className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <AppText className="text-sm font-medium text-foreground">{formatActionLabel(entry.action)}</AppText>
                    <AppText className="text-[10px]" color="$colorMuted">{format(new Date(entry.created_at), 'd MMM yyyy, HH:mm')}</AppText>
                    <span className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium',
                      entry.member_visible ? 'bg-primary/10 text-primary' : 'bg-background text-muted-foreground border border-border'
                    )}>
                      {entry.member_visible ? 'Member visible' : 'Internal only'}
                    </span>
                    {entry.report_id ? (
                      <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        Linked report
                      </span>
                    ) : null}
                  </div>
                  {entry.note ? (
                    <div className="rounded-xl border border-border bg-background px-3 py-3">
                      <AppText className="text-[10px] font-medium uppercase tracking-[0.08em]" color="$colorMuted">
                        Internal note
                      </AppText>
                      <AppText className="mt-2 whitespace-pre-wrap text-xs leading-relaxed" color="$colorMuted">{entry.note}</AppText>
                    </div>
                  ) : null}
                  {entry.member_message ? (
                    <div className="rounded-xl border border-primary/15 bg-primary/5 px-3 py-3">
                      <AppText className="text-[10px] font-medium uppercase tracking-[0.08em] text-primary">
                        Member notice
                      </AppText>
                      <AppText className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-foreground">{entry.member_message}</AppText>
                    </div>
                  ) : null}
                </AppCardBody>
              </AppCard>
            ))
          )}
        </MemberSection>

        <MemberSection title="Appeals">
          {appeals.length === 0 ? (
            <AppCard>
              <AppCardBody>
                <AppText fontSize="$sm" color="$colorMuted">
                  No appeals submitted yet.
                </AppText>
              </AppCardBody>
            </AppCard>
          ) : (
            appeals.map((appeal) => (
              <AppCard key={appeal.id}>
                <AppCardBody className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <AppText className="text-sm font-medium text-foreground">Appeal</AppText>
                    <AppText className="text-[10px]" color="$colorMuted">{format(new Date(appeal.created_at), 'd MMM yyyy, HH:mm')}</AppText>
                    <span className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium',
                      appeal.status === 'accepted'
                        ? 'bg-green-100 text-green-800'
                        : appeal.status === 'declined'
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-amber-100 text-amber-800'
                    )}>
                      {formatActionLabel(appeal.status)}
                    </span>
                  </div>
                  <AppText className="whitespace-pre-wrap text-xs leading-relaxed" color="$colorMuted">{appeal.message}</AppText>
                  {appeal.resolution_note ? (
                    <div className="rounded-xl border border-primary/15 bg-primary/5 px-3 py-3">
                      <AppText className="text-[10px] font-medium uppercase tracking-[0.08em] text-primary">
                        Resolution
                      </AppText>
                      <AppText className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-foreground">
                        {appeal.resolution_note}
                      </AppText>
                    </div>
                  ) : null}
                </AppCardBody>
              </AppCard>
            ))
          )}
        </MemberSection>
      </AppPage>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <AppCard className="gap-1 rounded-xl bg-background px-3 py-3">
      <AppText className="text-lg font-bold text-foreground">{value}</AppText>
      <AppText className="text-[11px]" color="$colorMuted">
        {label}
      </AppText>
    </AppCard>
  );
}
